import { useState, useRef, useEffect, useCallback } from 'react';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { supabase } from './lib/supabase';
import { createDeck, createIndexedData } from './utils/deckUtils';
import { DECKS } from './data/deckConfig';
import { useSound } from './hooks/useSound';
import { useAuth } from './hooks/useAuth';
import Paywall from './components/Paywall';
import FreeLimitModal from './components/FreeLimitModal';
import StreakCelebration from './components/StreakCelebration';
import AuthForm from './components/AuthForm';
import FlashCard from './components/FlashCard';
import MyCardsModal from './components/MyCardsModal';

const AppContent = () => {
  const { isPro, loading: subscriptionLoading, loginUser, logoutUser } = useSubscription();

  // Current deck selection
  const [currentDeckId, setCurrentDeckId] = useState('characters');

  // Custom cards state
  const [customCards, setCustomCards] = useState([]);

  // Per-deck progress tracking
  // Structure: { [deckId]: { known: Set, learning: Set } }
  const [deckProgress, setDeckProgress] = useState({});

  // Get current deck's known/learning sets
  const knownChars = deckProgress[currentDeckId]?.known || new Set();
  const learningChars = deckProgress[currentDeckId]?.learning || new Set();

  // Helper to update progress for current deck
  const setKnownChars = useCallback((updater) => {
    setDeckProgress(prev => {
      const current = prev[currentDeckId] || { known: new Set(), learning: new Set() };
      const newKnown = typeof updater === 'function' ? updater(current.known) : updater;
      return {
        ...prev,
        [currentDeckId]: { ...current, known: newKnown }
      };
    });
  }, [currentDeckId]);

  const setLearningChars = useCallback((updater) => {
    setDeckProgress(prev => {
      const current = prev[currentDeckId] || { known: new Set(), learning: new Set() };
      const newLearning = typeof updater === 'function' ? updater(current.learning) : updater;
      return {
        ...prev,
        [currentDeckId]: { ...current, learning: newLearning }
      };
    });
  }, [currentDeckId]);

  // Callback for when user progress is loaded (now receives per-deck data)
  const handleUserProgressLoad = useCallback((progressByDeck) => {
    // progressByDeck is an object: { deckId: { known: Set, learning: Set }, ... }
    setDeckProgress(prev => ({
      ...prev,
      ...progressByDeck
    }));
  }, []);

  // Auth hook
  const {
    user,
    guestMode,
    setGuestMode,
    authLoading,
    authMode,
    setAuthMode,
    authError,
    email,
    setEmail,
    password,
    setPassword,
    handleSignUp,
    handleSignIn,
    handleSignInWithGoogle,
    handleSignOut: authSignOut,
    continueAsGuest,
  } = useAuth(handleUserProgressLoad);

  const [showPaywall, setShowPaywall] = useState(false);
  const [showFreeLimitModal, setShowFreeLimitModal] = useState(false);

  // Show free limit modal for non-pro users on mount (once per session)
  useEffect(() => {
    if (!authLoading && !subscriptionLoading) {
      const hasSeenModal = sessionStorage.getItem('seenFreeLimitModal');
      const isFreeUser = (guestMode || user) && !isPro;

      if (isFreeUser && !hasSeenModal) {
        const timer = setTimeout(() => {
          setShowFreeLimitModal(true);
          sessionStorage.setItem('seenFreeLimitModal', 'true');
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [authLoading, user, guestMode, isPro, subscriptionLoading]);

  // Reset scroll position when auth state changes
  useEffect(() => {
    window.scrollTo(0, 0);
    const t1 = setTimeout(() => window.scrollTo(0, 0), 100);
    const t2 = setTimeout(() => window.scrollTo(0, 0), 300);
    const t3 = setTimeout(() => window.scrollTo(0, 0), 500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [user, guestMode]);

  // Load custom cards from Supabase
  const loadCustomCards = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('custom_cards')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        // Table might not exist yet - that's ok
        if (!error.message?.includes('does not exist')) {
          console.error('Error loading custom cards:', error);
        }
        return;
      }

      if (data) {
        setCustomCards(data.map(card => ({
          id: card.id,
          char: card.char,
          pinyin: card.pinyin,
          meaning: card.meaning,
          example: card.example || '',
          createdAt: card.created_at,
        })));
      }
    } catch (err) {
      console.error('Error loading custom cards:', err);
    }
  }, [user]);

  useEffect(() => {
    if (user && !guestMode) {
      loadCustomCards();
    }
  }, [user, guestMode, loadCustomCards]);

  const handleAddCustomCard = async (card) => {
    if (!user || guestMode) return;

    try {
      const { data, error } = await supabase
        .from('custom_cards')
        .insert({
          user_id: user.id,
          char: card.char,
          pinyin: card.pinyin,
          meaning: card.meaning,
          example: card.example || '',
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding custom card:', error);
        return;
      }

      const newCard = {
        id: data.id,
        char: data.char,
        pinyin: data.pinyin,
        meaning: data.meaning,
        example: data.example || '',
        createdAt: data.created_at,
      };

      setCustomCards(prev => [newCard, ...prev]);

      // Rebuild deck if on characters (to include new custom card)
      if (currentDeckId === 'characters') {
        setDeck(createDeck('characters', isPro, [...customCards, newCard]));
      }
    } catch (err) {
      console.error('Error adding custom card:', err);
    }
  };

  const handleDeleteCustomCard = async (cardId) => {
    if (!user || guestMode) return;

    if (!window.confirm('Delete this custom card?')) return;

    try {
      const { error } = await supabase
        .from('custom_cards')
        .delete()
        .eq('id', cardId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting custom card:', error);
        return;
      }

      const updatedCards = customCards.filter(c => c.id !== cardId);
      setCustomCards(updatedCards);

      // Rebuild deck if on characters
      if (currentDeckId === 'characters') {
        setDeck(createDeck('characters', isPro, updatedCards));
      }
    } catch (err) {
      console.error('Error deleting custom card:', err);
    }
  };

  // Flashcard deck state
  const [deck, setDeck] = useState(() => createDeck('characters', isPro, []));

  // Effect to update deck when isPro or currentDeckId changes
  useEffect(() => {
    if (currentDeckId === 'characters') {
      setDeck(createDeck('characters', isPro, customCards));
    } else {
      setDeck(createDeck(currentDeckId, isPro, []));
    }
    setCurrentIndex(0);
    setIsFlipped(false);
  }, [isPro, currentDeckId, customCards]);

  const handleDeckChange = (newDeckId) => {
    setCurrentDeckId(newDeckId);
    setCurrentIndex(0);
    setIsFlipped(false);
    setStreak(0);
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [streak, setStreak] = useState(0);
  const [showStreakCelebration, setShowStreakCelebration] = useState(null);
  const [showMyCards, setShowMyCards] = useState(false);

  // Theme state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true;
  });

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const theme = {
    bg: isDarkMode
      ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
      : '#4FAEA5',
    cardBg: isDarkMode
      ? 'linear-gradient(145deg, #1e293b, #0f172a)'
      : 'linear-gradient(145deg, #ffffff, #f0f9f8)',
    cardBorder: isDarkMode
      ? 'rgba(255,255,255,0.1)'
      : 'rgba(0,0,0,0.1)',
    text: isDarkMode ? '#f8fafc' : '#ffffff',
    textMuted: isDarkMode ? '#94a3b8' : 'rgba(255,255,255,0.9)',
    textSecondary: isDarkMode ? '#64748b' : 'rgba(255,255,255,0.7)',
    buttonBg: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
    buttonText: isDarkMode ? '#94a3b8' : '#ffffff',
  };

  const cardRef = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const startTime = useRef(0);

  const userRef = useRef(user);
  const guestModeRef = useRef(guestMode);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    guestModeRef.current = guestMode;
  }, [guestMode]);

  const { playCorrect, playStreak, playWrong } = useSound();

  // Sync user with RevenueCat for cross-platform subscriptions
  useEffect(() => {
    if (user?.id && !guestMode) {
      loginUser(user.id);
    }
  }, [user?.id, guestMode, loginUser]);

  const handleSignOut = async () => {
    await authSignOut();
    await logoutUser();
    setStreak(0);
    setDeckProgress({});
    setCustomCards([]);
  };

  // Save progress to database
  const saveProgress = useCallback(async (charIndex, isKnown) => {
    const currentUser = userRef.current;
    const isGuestMode = guestModeRef.current;

    if (!currentUser || isGuestMode) return;

    try {
      await supabase.from('user_progress').upsert({
        user_id: currentUser.id,
        character_index: charIndex,
        deck_id: currentDeckId,
        known: isKnown,
        last_seen: new Date().toISOString()
      }, {
        onConflict: 'user_id,character_index'
      });
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  }, [currentDeckId]);

  const currentCard = deck[currentIndex];
  const progress = deck.length > 0
    ? ((knownChars.size + learningChars.size) / deck.length) * 100
    : 0;

  // Get current deck config
  const currentDeckConfig = DECKS[currentDeckId];

  // Get indexed data for progress display
  const currentDeckData = createIndexedData(currentDeckId);

  const handleReshuffle = useCallback(() => {
    if (currentDeckId === 'characters') {
      setDeck(createDeck('characters', isPro, customCards));
    } else {
      setDeck(createDeck(currentDeckId, isPro, []));
    }
    setCurrentIndex(0);
    setStreak(0);
    setIsFlipped(false);
  }, [isPro, currentDeckId, customCards]);

  const handleSwipe = useCallback((direction) => {
    if (!currentCard) return;

    setSwipeDirection(direction);
    const charIndex = currentCard.originalIndex;

    // Save progress to database (skip for custom cards in local state)
    if (currentCard.deckId !== 'custom') {
      saveProgress(charIndex, direction === 'right');
    }

    // Update character lists
    if (direction === 'right') {
      setKnownChars(prev => new Set([...prev, charIndex]));
      setLearningChars(prev => {
        const next = new Set(prev);
        next.delete(charIndex);
        return next;
      });
    } else {
      setLearningChars(prev => new Set([...prev, charIndex]));
      setKnownChars(prev => {
        const next = new Set(prev);
        next.delete(charIndex);
        return next;
      });
    }

    setTimeout(() => {
      if (direction === 'right') {
        const newStreak = streak + 1;
        setStreak(newStreak);

        if (newStreak === 10 || newStreak === 50 || newStreak === 100) {
          playStreak(newStreak);
          setShowStreakCelebration(newStreak);
        } else {
          playCorrect();
        }
      } else {
        setStreak(0);
        playWrong();
      }

      setSwipeDirection(null);
      setIsFlipped(false);
      setDragX(0);

      if (currentIndex < deck.length - 1) {
        setCurrentIndex(i => i + 1);
      } else {
        handleReshuffle();
      }
    }, 300);
  }, [currentCard, currentIndex, deck.length, streak, playCorrect, playStreak, playWrong, handleReshuffle, saveProgress, setKnownChars, setLearningChars]);

  const handleResetProgress = async () => {
    if (!window.confirm(`Reset all progress for ${currentDeckConfig?.name || 'this deck'}? This cannot be undone.`)) {
      return;
    }

    if (user && !guestMode) {
      await supabase
        .from('user_progress')
        .delete()
        .eq('user_id', user.id)
        .eq('deck_id', currentDeckId);
    }

    setDeckProgress(prev => ({
      ...prev,
      [currentDeckId]: { known: new Set(), learning: new Set() }
    }));

    handleReshuffle();
  };

  const handlePointerDown = (e) => {
    setIsDragging(true);
    startX.current = e.clientX || e.touches?.[0]?.clientX || 0;
    startY.current = e.clientY || e.touches?.[0]?.clientY || 0;
    startTime.current = Date.now();
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    if (e.cancelable) e.preventDefault();
    const currentX = e.clientX || e.touches?.[0]?.clientX || 0;
    const diff = currentX - startX.current;
    setDragX(diff);
  };

  const handlePointerUp = (e) => {
    if (!isDragging) return;

    const endX = e.clientX || e.changedTouches?.[0]?.clientX || 0;
    const endY = e.clientY || e.changedTouches?.[0]?.clientY || 0;
    const diffX = endX - startX.current;
    const diffY = endY - startY.current;
    const elapsed = Date.now() - startTime.current;

    setIsDragging(false);

    if (Math.abs(diffX) < 22 && Math.abs(diffY) < 22 && elapsed < 500) {
      setIsFlipped(f => !f);
      setDragX(0);
      return;
    }

    if (Math.abs(diffX) > 100) {
      handleSwipe(diffX > 0 ? 'right' : 'left');
    } else {
      setDragX(0);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (swipeDirection) return;

      if (e.key === 'ArrowRight') {
        handleSwipe('right');
      } else if (e.key === 'ArrowLeft') {
        handleSwipe('left');
      } else if (e.key === ' ' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsFlipped(f => !f);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [swipeDirection, handleSwipe]);

  // Loading state
  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif',
      }}>
        <div style={{ color: '#94a3b8', fontSize: '18px' }}>Loading...</div>
      </div>
    );
  }

  // Auth screen
  if (!user && !guestMode) {
    return (
      <AuthForm
        authMode={authMode}
        setAuthMode={setAuthMode}
        authError={authError}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        handleSignIn={handleSignIn}
        handleSignUp={handleSignUp}
        handleSignInWithGoogle={handleSignInWithGoogle}
        continueAsGuest={continueAsGuest}
      />
    );
  }

  // No cards in deck
  if (deck.length === 0 || !currentCard) {
    return (
      <div className="app-root" style={{
        background: theme.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '16px',
        padding: '32px',
        textAlign: 'center',
        minHeight: '100vh',
        fontFamily: '"Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif',
      }}>
        <div style={{ fontSize: '48px' }}>📭</div>
        <h2 style={{ color: theme.text, margin: 0 }}>No Cards Available</h2>
        <p style={{ color: theme.textMuted }}>
          {currentDeckId === 'custom'
            ? 'Add some custom cards to get started!'
            : 'This deck is empty or requires a subscription.'}
        </p>
        <button
          onClick={() => setShowMyCards(true)}
          style={{
            padding: '12px 24px',
            background: currentDeckConfig?.color || '#3b82f6',
            border: 'none',
            borderRadius: '12px',
            color: 'white',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          {currentDeckId === 'custom' ? 'Add Custom Cards' : 'Choose Another Deck'}
        </button>

        {showMyCards && (
          <MyCardsModal
            onClose={() => setShowMyCards(false)}
            currentDeckId={currentDeckId}
            onDeckChange={(deckId) => {
              handleDeckChange(deckId);
              setShowMyCards(false);
            }}
            knownChars={knownChars}
            learningChars={learningChars}
            isPro={isPro}
            isAuthenticated={!!user && !guestMode}
            customCards={customCards}
            onAddCustomCard={handleAddCustomCard}
            onDeleteCustomCard={handleDeleteCustomCard}
            onResetProgress={handleResetProgress}
            deckData={currentDeckData}
          />
        )}
      </div>
    );
  }

  return (
    <div className="app-root" style={{
      background: theme.bg,
      transition: 'background 0.3s ease',
    }}>
      {/* Streak Celebration */}
      {showStreakCelebration && (
        <StreakCelebration
          milestone={showStreakCelebration}
          onComplete={() => setShowStreakCelebration(null)}
        />
      )}

      {/* My Cards Modal */}
      {showMyCards && (
        <MyCardsModal
          onClose={() => setShowMyCards(false)}
          currentDeckId={currentDeckId}
          onDeckChange={(deckId) => {
            handleDeckChange(deckId);
            setShowMyCards(false);
          }}
          knownChars={knownChars}
          learningChars={learningChars}
          isPro={isPro}
          isAuthenticated={!!user && !guestMode}
          customCards={customCards}
          onAddCustomCard={handleAddCustomCard}
          onDeleteCustomCard={handleDeleteCustomCard}
          onResetProgress={handleResetProgress}
          deckData={currentDeckData}
        />
      )}

      {/* Header styles */}
      <style>{`
        .app-root {
          height: 100vh;
          height: 100dvh;
          width: 100%;
          position: fixed;
          top: 0;
          left: 0;
          overflow: hidden;
          overscroll-behavior: none;
          touch-action: none;

          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;

          padding-top: calc(16px + env(safe-area-inset-top));
          padding-bottom: calc(16px + env(safe-area-inset-bottom));
          padding-left: 20px;
          padding-right: 20px;
          box-sizing: border-box;

          font-family: "Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif;
        }

        .top-section {
          width: 100%;
          max-width: 600px;
          display: flex;
          flex-direction: column;
          align-items: center;
          flex-shrink: 0;
        }

        .middle-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          min-height: 0;
          padding: 12px 0;
        }

        .bottom-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex-shrink: 0;
          width: 100%;
        }

        .app-header {
          position: relative;
          width: 100%;
          max-width: 600px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          z-index: 10;
          margin-bottom: 12px;
        }
        .progress-bar-container {
          position: relative;
          width: 100%;
          max-width: 600px;
          z-index: 9;
        }
        .header-stats {
          font-size: 14px;
          white-space: nowrap;
        }
        .header-buttons {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .header-btn {
          padding: 6px 12px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          font-size: 12px;
          white-space: nowrap;
        }
        .header-btn-text {
          display: inline;
        }
        .header-btn-icon {
          display: none;
        }
        .card-stack {
          position: relative;
          width: min(320px, 85vw);
          height: min(440px, 50vh);
          perspective: 1000px;
          z-index: 5;
          flex-shrink: 0;
        }
        .action-buttons {
          display: flex;
          gap: 48px;
          margin-top: clamp(16px, 3vh, 28px);
          z-index: 10;
          flex-shrink: 0;
        }
        .action-btn {
          width: 72px;
          height: 72px;
          font-size: 32px;
        }
        .card-char-front {
          font-size: clamp(100px, 22vw, 160px);
        }
        .card-char-back {
          font-size: clamp(48px, 12vw, 64px);
        }
        .card-char-front-phrase {
          font-size: clamp(28px, 7vw, 42px);
        }
        .card-char-back-phrase {
          font-size: clamp(22px, 5vw, 32px);
        }
        .card-pinyin {
          font-size: clamp(22px, 5vw, 28px);
        }
        .card-meaning {
          font-size: clamp(14px, 3.5vw, 16px);
        }
        .card-example {
          font-size: clamp(11px, 3vw, 13px);
        }
        .pro-unlock-btn {
          margin-top: clamp(8px, 2vh, 16px);
        }
        .instructions-text {
          font-size: 13px;
          margin-bottom: 4px;
        }

        @media (max-width: 600px) {
          .app-root {
            padding-left: 16px;
            padding-right: 16px;
          }
          .app-header {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
            padding: 10px 12px;
            border-radius: 16px;
            background: rgba(15, 23, 42, 0.55);
            border: 1px solid rgba(255, 255, 255, 0.12);
            box-shadow: 0 8px 24px rgba(0,0,0,0.3);
            backdrop-filter: blur(12px);
            margin-bottom: 12px;
          }
          .header-stats {
            font-size: 13px;
            text-align: center;
          }
          .header-buttons {
            justify-content: center;
            gap: 6px;
            flex-wrap: nowrap;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .header-buttons::-webkit-scrollbar {
            display: none;
          }
          .header-btn {
            padding: 6px 10px;
            font-size: 11px;
            border: 1px solid rgba(255,255,255,0.12);
            background-clip: padding-box;
          }
          .header-btn-text {
            display: none;
          }
          .header-btn-icon {
            display: inline;
          }
          .card-stack {
            width: min(320px, 85vw);
            height: min(440px, 48vh);
          }
          .action-buttons {
            gap: 40px;
          }
          .action-btn {
            width: 68px;
            height: 68px;
            font-size: 30px;
          }
          .instructions-text {
            font-size: 12px;
          }
        }

        @media (max-width: 375px) {
          .app-root {
            padding-left: 12px;
            padding-right: 12px;
            padding-top: calc(12px + env(safe-area-inset-top));
          }
          .app-header {
            padding: 8px 10px;
            gap: 6px;
            margin-bottom: 10px;
            border-radius: 14px;
          }
          .header-stats {
            font-size: 12px;
          }
          .header-btn {
            padding: 5px 8px;
            font-size: 10px;
          }
          .card-stack {
            width: min(290px, 88vw);
            height: min(380px, 46vh);
          }
          .action-buttons {
            gap: 32px;
          }
          .action-btn {
            width: 60px;
            height: 60px;
            font-size: 26px;
          }
          .instructions-text {
            font-size: 11px;
          }
        }

        @media (min-height: 800px) and (max-width: 600px) {
          .card-stack {
            width: min(340px, 88vw);
            height: min(480px, 52vh);
          }
          .action-btn {
            width: 72px;
            height: 72px;
            font-size: 32px;
          }
          .action-buttons {
            gap: 48px;
            margin-top: 24px;
          }
          .middle-section {
            padding: 20px 0;
          }
        }

        @media (min-height: 900px) and (max-width: 500px) {
          .card-stack {
            width: min(350px, 90vw);
            height: min(520px, 54vh);
          }
          .action-buttons {
            margin-top: 28px;
          }
        }

        @media (max-height: 700px) {
          .app-root {
            padding-top: calc(10px + env(safe-area-inset-top));
            padding-bottom: calc(10px + env(safe-area-inset-bottom));
          }
          .card-stack {
            height: min(360px, 46vh);
          }
          .action-btn {
            width: 60px;
            height: 60px;
            font-size: 26px;
          }
          .action-buttons {
            gap: 32px;
          }
          .middle-section {
            padding: 8px 0;
          }
        }

        @media (max-height: 600px) {
          .app-root {
            padding-top: calc(8px + env(safe-area-inset-top));
            padding-bottom: calc(8px + env(safe-area-inset-bottom));
          }
          .app-header {
            margin-bottom: 8px;
            padding: 6px 10px;
          }
          .card-stack {
            height: min(300px, 44vh);
          }
          .action-btn {
            width: 54px;
            height: 54px;
            font-size: 24px;
          }
          .action-buttons {
            gap: 28px;
          }
          .instructions-text {
            font-size: 10px;
          }
          .middle-section {
            padding: 4px 0;
          }
        }

        @media (max-height: 500px) {
          .card-stack {
            height: min(240px, 42vh);
          }
          .action-btn {
            width: 48px;
            height: 48px;
            font-size: 20px;
          }
          .instructions-text {
            display: none;
          }
        }

        @media (max-width: 375px) and (max-height: 650px) {
          .card-stack {
            width: min(260px, 85vw);
            height: min(280px, 44vh);
          }
        }
      `}</style>

      {/* TOP SECTION: Header + Progress */}
      <div className="top-section">
        <div className="app-header">
          <div className="header-stats" style={{ color: theme.textMuted }}>
            <span style={{ color: '#22c55e', fontWeight: 'bold' }}>{knownChars.size}</span> 记住 ·
            <span style={{ color: '#ef4444', fontWeight: 'bold', marginLeft: '8px' }}>{learningChars.size}</span> 学习中
            {guestMode && <span style={{ marginLeft: '8px', color: theme.textSecondary }}>(Guest)</span>}
          </div>
          <div className="header-buttons">
            {user && (
              <button
                onClick={handleSignOut}
                className="header-btn"
                style={{ background: theme.buttonBg, color: theme.buttonText }}
              >
                <span className="header-btn-text">Sign out</span>
                <span className="header-btn-icon">👋</span>
              </button>
            )}
            {guestMode && (
              <button
                onClick={() => setGuestMode(false)}
                className="header-btn"
                style={{ background: theme.buttonBg, color: theme.buttonText }}
              >
                <span className="header-btn-text">Sign in</span>
                <span className="header-btn-icon">🔑</span>
              </button>
            )}
            {/* Deck selector button */}
            <button
              onClick={() => setShowMyCards(true)}
              className="header-btn"
              style={{
                background: `${currentDeckConfig?.color || '#3b82f6'}30`,
                color: currentDeckConfig?.color || '#3b82f6',
                border: `1px solid ${currentDeckConfig?.color || '#3b82f6'}50`,
              }}
            >
              <span className="header-btn-text">{currentDeckConfig?.name || 'Deck'}</span>
              <span className="header-btn-icon">{currentDeckConfig?.icon || '📚'}</span>
            </button>
            <button
              onClick={handleReshuffle}
              className="header-btn"
              style={{ background: theme.buttonBg, color: theme.buttonText }}
            >
              <span className="header-btn-text">Reshuffle</span>
              <span className="header-btn-icon">🔀</span>
            </button>
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="header-btn"
              style={{ background: theme.buttonBg, color: theme.buttonText }}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
            {streak > 0 && (
              <div className="header-btn header-streak" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: streak >= 10
                  ? 'linear-gradient(135deg, #f59e0b, #ef4444)'
                  : 'rgba(251, 191, 36, 0.2)',
                animation: streak >= 5 ? 'pulse 1s ease-in-out infinite' : 'none',
              }}>
                <span style={{ fontSize: '12px' }}>🔥</span>
                <span style={{
                  color: streak >= 10 ? 'white' : '#fbbf24',
                  fontWeight: 'bold',
                  fontSize: '12px',
                }}>
                  {streak}
                </span>
              </div>
            )}
            <div className="header-btn header-count" style={{ background: theme.buttonBg, color: theme.textSecondary }}>
              {currentIndex + 1}/{deck.length}
            </div>
          </div>
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}</style>

        {/* Progress bar */}
        <div className="progress-bar-container" style={{
          height: '4px',
          background: '#1e293b',
          borderRadius: '2px',
          overflow: 'hidden',
          zIndex: 9,
          pointerEvents: 'none',
        }}>
          <div style={{
            width: `${progress}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${currentDeckConfig?.color || '#22c55e'}, #3b82f6)`,
            borderRadius: '2px',
            transition: 'width 0.3s ease',
          }} />
        </div>
      </div>

      {/* MIDDLE SECTION: Card + Action Buttons */}
      <div className="middle-section">

      {/* Card Stack Visual */}
      <div className="card-stack">
        {[2, 1].map((offset) => (
          <div key={offset} style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: isDarkMode
              ? 'linear-gradient(145deg, #1e293b, #334155)'
              : 'linear-gradient(145deg, #d1d5db, #e5e7eb)',
            borderRadius: '24px',
            transform: `translateY(${offset * 8}px) scale(${1 - offset * 0.05})`,
            opacity: 0.5 - offset * 0.15,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }} />
        ))}

        <FlashCard
          ref={cardRef}
          currentCard={currentCard}
          currentIndex={currentIndex}
          deckLength={deck.length}
          isFlipped={isFlipped}
          isDragging={isDragging}
          dragX={dragX}
          swipeDirection={swipeDirection}
          isDarkMode={isDarkMode}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={() => { if (isDragging) { setIsDragging(false); setDragX(0); } }}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
        />
      </div>

      {/* Action buttons */}
      <div className="action-buttons">
        <button
          className="action-btn"
          onClick={() => handleSwipe('left')}
          style={{
            borderRadius: '50%',
            border: '3px solid #ef4444',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 20px rgba(239,68,68,0.3)',
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#ef4444';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        >
          ✕
        </button>

        <button
          className="action-btn"
          onClick={() => handleSwipe('right')}
          style={{
            borderRadius: '50%',
            border: '3px solid #22c55e',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 20px rgba(34,197,94,0.3)',
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#22c55e';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        >
          ✓
        </button>
      </div>
      </div>

      {/* BOTTOM SECTION: Instructions + Pro Button */}
      <div className="bottom-section">
        <div className="instructions-text" style={{
          color: theme.textSecondary,
          textAlign: 'center',
          opacity: 0.7,
        }}>
          ← 再学习 &nbsp;·&nbsp; 点击翻转 &nbsp;·&nbsp; 记住了 →
        </div>
        {!isPro && (
          <button
            className="pro-unlock-btn"
            onClick={() => setShowPaywall(true)}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #ffd700, #f59e0b)',
              borderRadius: '24px',
              border: 'none',
              color: '#713f12',
              fontWeight: 'bold',
              fontSize: '16px',
              cursor: 'pointer',
              boxShadow: '0 6px 16px rgba(245, 158, 11, 0.4)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              animation: 'pulse 2s infinite',
            }}
          >
            <span>👑</span> Unlock Pro
          </button>
        )}
      </div>

      {showFreeLimitModal && (
        <FreeLimitModal
          onClose={() => setShowFreeLimitModal(false)}
          onUpgrade={() => {
            setShowFreeLimitModal(false);
            setShowPaywall(true);
          }}
        />
      )}

      {showPaywall && <Paywall onClose={() => setShowPaywall(false)} />}
    </div>
  );
};

export default function ChineseFlashcards() {
  return (
    <SubscriptionProvider>
      <AppContent />
    </SubscriptionProvider>
  );
}
